// Yappa Buyback — trade tray custom elements + local store + API client.
//
// Design principles:
//   - Vanilla Custom Elements (no framework), matches theme conventions.
//   - Idempotent load: safe to be included twice (registration guards).
//   - Zero DOM assumptions on module boot; everything hydrates on
//     `connectedCallback`.
//   - Public API on `window.YappaBuyback` for other theme code to call.
//   - All /apps/buyback/* requests flow through `YappaBuyback.apiCall` —
//     one place for retry/timeout/error handling policy.
//
// Custom elements:
//   <trade-drawer>       Drawer container (extends theme's DrawerElement).
//                        Auto-opens on `trade:refresh` event with { open: true }.
//   <trade-count>        Small badge in the header showing item count.
//   <trade-items>        Container that re-renders when the local store changes.
//   <yappa-price-pair>   Store Credit primary + Cash secondary, used everywhere.
//
// Storage:
//   localStorage.yappa_buyback_trade  =  { items: [...], updated_at: ISO }
//
// Custom events (all bubbling, on document):
//   trade:updated  detail: { count, total_cash_nzd, total_credit_nzd }
//   trade:refresh  detail: { open: bool }  — asks drawer to refresh + optionally open
//   trade:error    detail: { code, message, retryable }
//   trade:success  detail: { message }
//
// Public API — window.YappaBuyback:
//   .getTrade()                → { items, updated_at }
//   .addItem(item)             → new state
//   .removeItem(id)            → new state
//   .clear()                   → clears store
//   .totals(trade?)            → { count, total_cash_nzd, total_credit_nzd }
//   .formatMoney(cents)        → "$xx.xx"
//   .openDrawer()              → dispatches trade:refresh { open: true }
//   .apiCall(path, opts)       → fetch wrapper with retry + timeout + toast on error
//   .toast(msg, kind?)         → 'info' | 'success' | 'error'
//
// Phase 3 hooks the search UI + calls apiCall('/apps/buyback/quote', {…}).

(function () {
  'use strict';

  const STORAGE_KEY = 'yappa_buyback_trade';
  const API_TIMEOUT_MS = 12_000;
  const API_MAX_RETRIES = 2;
  const API_RETRY_DELAY_MS = 400;
  const TOAST_TTL_MS = 4200;

  // ---------- Store ----------

  function getTrade() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return emptyTrade();
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) return emptyTrade();
      return parsed;
    } catch (_) {
      return emptyTrade();
    }
  }

  function emptyTrade() {
    return { items: [], updated_at: null };
  }

  function setTrade(state) {
    const next = {
      items: Array.isArray(state.items) ? state.items : [],
      updated_at: new Date().toISOString(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      // localStorage quota exceeded or in private-mode Safari — silently
      // continue with the in-memory copy. User's list won't survive
      // reload, but the current session works.
      toast(
        "Your browser can't save a draft trade. You can still submit but " +
          "will need to complete before reloading.",
        'error',
      );
    }
    dispatchUpdated(next);
    return next;
  }

  function addItem(item) {
    const trade = getTrade();
    const id = item.id || `yb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    trade.items.push({ ...item, id });
    return setTrade(trade);
  }

  function removeItem(id) {
    const trade = getTrade();
    trade.items = trade.items.filter((it) => it.id !== id);
    return setTrade(trade);
  }

  function clear() {
    return setTrade({ items: [] });
  }

  function totals(trade) {
    const t = trade || getTrade();
    let cash = 0;
    let credit = 0;
    for (const it of t.items) {
      cash += Number(it.offer_cash_nzd) || 0;
      credit += Number(it.offer_credit_nzd) || 0;
    }
    return { count: t.items.length, total_cash_nzd: cash, total_credit_nzd: credit };
  }

  function dispatchUpdated(trade) {
    const t = totals(trade);
    document.dispatchEvent(
      new CustomEvent('trade:updated', { bubbles: true, detail: t }),
    );
  }

  function formatMoney(cents) {
    const n = Number(cents) || 0;
    return `$${(n / 100).toFixed(2)}`;
  }

  // Sync across tabs so the header count + drawer stay in step if the
  // user opens /sell in two tabs.
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) dispatchUpdated(getTrade());
  });

  // ---------- API client ----------
  //
  // One place for the entire buyback flow's HTTP policy:
  //   - AbortController timeout
  //   - JSON body handling
  //   - Retries only on network errors or 5xx (never on 4xx)
  //   - Structured error events + user-visible toast on final failure
  //   - Offline detection short-circuits before we waste a request

  async function apiCall(path, opts = {}) {
    const { method = 'GET', body, headers = {}, retries = API_MAX_RETRIES } = opts;

    if (!navigator.onLine) {
      const err = { code: 'offline', message: "You're offline. Reconnect and try again.", retryable: true };
      dispatchError(err);
      throw err;
    }

    const init = {
      method,
      headers: {
        'accept': 'application/json',
        ...(body ? { 'content-type': 'application/json' } : {}),
        ...headers,
      },
      credentials: 'include',
    };
    if (body !== undefined) init.body = JSON.stringify(body);

    let lastError = null;
    for (let attempt = 0; attempt <= retries; attempt++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), API_TIMEOUT_MS);
      init.signal = controller.signal;

      try {
        const res = await fetch(path, init);
        clearTimeout(timer);

        let payload = null;
        try {
          payload = await res.json();
        } catch (_) {
          // Non-JSON error response — treat as opaque.
          payload = null;
        }

        if (res.ok) return payload;

        // 4xx — don't retry, surface directly.
        if (res.status >= 400 && res.status < 500) {
          const err = {
            code: (payload && payload.error) || `http_${res.status}`,
            message: (payload && payload.message) || defaultErrorMessage(res.status),
            status: res.status,
            retryable: false,
            details: payload,
          };
          dispatchError(err);
          throw err;
        }

        // 5xx — retryable.
        lastError = {
          code: `http_${res.status}`,
          message: defaultErrorMessage(res.status),
          status: res.status,
          retryable: true,
        };
      } catch (err) {
        clearTimeout(timer);

        // Non-retryable errors bubbled up from the 4xx branch above.
        if (err && err.retryable === false) throw err;

        // AbortError → timeout.
        if (err && err.name === 'AbortError') {
          lastError = { code: 'timeout', message: 'Request timed out. Retrying…', retryable: true };
        } else if (err instanceof TypeError) {
          // Fetch network failure.
          lastError = { code: 'network', message: 'Network error. Retrying…', retryable: true };
        } else {
          lastError = { code: 'unknown', message: 'Something went wrong.', retryable: false };
          dispatchError(lastError);
          throw lastError;
        }
      }

      // Backoff before retry.
      if (attempt < retries) {
        await sleep(API_RETRY_DELAY_MS * Math.pow(2, attempt));
      }
    }

    // All retries exhausted.
    const finalErr = {
      ...(lastError || { code: 'unknown', message: 'Something went wrong.' }),
      retryable: false,
    };
    finalErr.message = `${finalErr.message} If it keeps happening, refresh the page.`;
    dispatchError(finalErr);
    throw finalErr;
  }

  function defaultErrorMessage(status) {
    if (status === 401) return 'Sign in to continue.';
    if (status === 403) return "You can't do that.";
    if (status === 404) return "We couldn't find that.";
    if (status === 409) return 'Already done — refresh to see the latest.';
    if (status === 429) return 'Too many requests. Wait a moment.';
    if (status >= 500) return "Our server hiccuped. We'll retry.";
    return 'Something went wrong.';
  }

  function dispatchError(err) {
    document.dispatchEvent(
      new CustomEvent('trade:error', { bubbles: true, detail: err }),
    );
    toast(err.message, 'error');
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // ---------- Toast surface ----------
  //
  // A single fixed region at bottom-right that queues short-lived
  // messages. Lazy — the region is only added to the DOM the first
  // time a toast fires.

  let toastRegion = null;
  function toast(message, kind = 'info') {
    if (!message) return;
    ensureToastRegion();
    const el = document.createElement('div');
    el.className = `yb-toast yb-toast--${kind}`;
    el.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    el.textContent = message;
    toastRegion.appendChild(el);
    setTimeout(() => {
      el.style.transition = 'opacity 0.2s ease';
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 220);
    }, TOAST_TTL_MS);
  }
  function ensureToastRegion() {
    if (toastRegion && document.body.contains(toastRegion)) return;
    toastRegion = document.createElement('div');
    toastRegion.className = 'yb-toast-region';
    toastRegion.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastRegion);
  }

  // ---------- Custom elements ----------

  // <trade-drawer> — extends DrawerElement for free open/close, backdrop,
  // scroll-lock, keyboard escape, focus trap. Guards on the class being
  // available (theme.js loads first, but guard anyway).
  if (
    !customElements.get('trade-drawer') &&
    typeof DrawerElement !== 'undefined'
  ) {
    class TradeDrawer extends DrawerElement {
      connectedCallback() {
        super.connectedCallback();
        this.sectionId = this.dataset.sectionId;
        this._onRefresh = this._onRefresh.bind(this);
        document.addEventListener('trade:refresh', this._onRefresh);
      }
      disconnectedCallback() {
        if (super.disconnectedCallback) super.disconnectedCallback();
        document.removeEventListener('trade:refresh', this._onRefresh);
      }
      _onRefresh(event) {
        if (event.detail && event.detail.open) this.show();
      }
    }
    customElements.define('trade-drawer', TradeDrawer);
  }

  // <trade-count> — reactive header badge. Hydrates on connect and on
  // every trade:updated event.
  if (!customElements.get('trade-count')) {
    class TradeCount extends HTMLElement {
      connectedCallback() {
        this._onUpdated = this._onUpdated.bind(this);
        document.addEventListener('trade:updated', this._onUpdated);
        this._render(totals());
      }
      disconnectedCallback() {
        document.removeEventListener('trade:updated', this._onUpdated);
      }
      _onUpdated(e) { this._render(e.detail); }
      _render({ count }) {
        this.textContent = count;
        if (count > 0) this.removeAttribute('hidden');
        else this.setAttribute('hidden', '');
      }
    }
    customElements.define('trade-count', TradeCount);
  }

  // <trade-items> — reactive list body. Renders the empty state or a
  // compact list of item rows. Also toggles the footer visibility.
  if (!customElements.get('trade-items')) {
    class TradeItems extends HTMLElement {
      connectedCallback() {
        this._onUpdated = this._onUpdated.bind(this);
        document.addEventListener('trade:updated', this._onUpdated);
        this._render(getTrade());
      }
      disconnectedCallback() {
        document.removeEventListener('trade:updated', this._onUpdated);
      }
      _onUpdated() { this._render(getTrade()); }
      _render(trade) {
        const drawer =
          this.closest('trade-drawer') || document.getElementById('TradeDrawer');
        const footer = drawer && drawer.querySelector('[data-trade-footer]');

        if (!trade.items.length) {
          this.innerHTML = renderEmptyState();
          if (footer) footer.setAttribute('hidden', '');
          return;
        }

        const t = totals(trade);
        this.innerHTML = `<ul class="trade-list">${trade.items.map(renderRow).join('')}</ul>`;

        if (footer) {
          footer.removeAttribute('hidden');
          updateFooterTotals(footer, t);
        }

        // Delegate remove-button clicks.
        this.querySelectorAll('[data-trade-remove]').forEach((btn) => {
          btn.addEventListener('click', () => {
            removeItem(btn.getAttribute('data-trade-remove'));
          });
        });
      }
    }
    customElements.define('trade-items', TradeItems);
  }

  // <yappa-price-pair> — mostly a styling wrapper; the JS side just
  // reads data-cash + data-credit and keeps the two spans in sync when
  // updated by outside code. Exposed via setAttribute so any component
  // can re-render totals.
  if (!customElements.get('yappa-price-pair')) {
    class YappaPricePair extends HTMLElement {
      static get observedAttributes() {
        return ['data-cash', 'data-credit'];
      }
      connectedCallback() { this._render(); }
      attributeChangedCallback() { this._render(); }
      _render() {
        const credit = Number(this.getAttribute('data-credit')) || 0;
        const cash = Number(this.getAttribute('data-cash')) || 0;
        const creditEl = this.querySelector('.yappa-price-pair__credit');
        const cashEl = this.querySelector('.yappa-price-pair__cash');
        if (creditEl) creditEl.textContent = `${formatMoney(credit)} store credit`;
        if (cashEl) cashEl.textContent = `${formatMoney(cash)} cash`;
      }
    }
    customElements.define('yappa-price-pair', YappaPricePair);
  }

  // ---------- Row rendering ----------
  // Kept as pure functions so trade-items can call them synchronously.

  function renderEmptyState() {
    return `
      <div class="trade-empty">
        <p class="trade-empty__title">Your trade is empty</p>
        <p class="trade-empty__body">Search or scan a game above to get a quote.</p>
      </div>
    `;
  }

  function renderRow(item) {
    const title = escapeHtml(item.title || 'Untitled');
    const platform = item.platform ? escapeHtml(item.platform) : '';
    const cond = item.condition ? escapeHtml(item.condition) : '';
    const meta = [platform, cond].filter(Boolean).join(' · ');
    const img = item.image_url
      ? `<img class="trade-row__thumb" src="${escapeHtml(item.image_url)}" alt="" width="48" height="48" loading="lazy" decoding="async">`
      : `<div class="trade-row__thumb" aria-hidden="true"></div>`;

    return `
      <li class="trade-row">
        ${img}
        <div class="trade-row__body">
          <div class="trade-row__title">${title}</div>
          ${meta ? `<div class="trade-row__meta">${meta}</div>` : ''}
          <div class="trade-row__prices">
            <yappa-price-pair
              data-cash="${Number(item.offer_cash_nzd) || 0}"
              data-credit="${Number(item.offer_credit_nzd) || 0}">
              <span class="yappa-price-pair__credit"></span>
              <span class="yappa-price-pair__cash"></span>
            </yappa-price-pair>
          </div>
        </div>
        <button type="button"
                class="trade-row__remove"
                data-trade-remove="${escapeHtml(item.id)}"
                aria-label="Remove ${title}">
          &times;
        </button>
      </li>
    `;
  }

  function updateFooterTotals(footer, t) {
    const pair = footer.querySelector('yappa-price-pair');
    if (!pair) return;
    pair.setAttribute('data-cash', String(t.total_cash_nzd));
    pair.setAttribute('data-credit', String(t.total_credit_nzd));
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
    );
  }

  // ---------- Public API ----------
  window.YappaBuyback = Object.assign(window.YappaBuyback || {}, {
    getTrade,
    addItem,
    removeItem,
    clear,
    totals,
    formatMoney,
    apiCall,
    toast,
    openDrawer() {
      document.dispatchEvent(
        new CustomEvent('trade:refresh', { detail: { open: true } }),
      );
    },
  });

  // Initial dispatch so <trade-count> etc. show correct counts on
  // first paint.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => dispatchUpdated(getTrade()));
  } else {
    dispatchUpdated(getTrade());
  }
})();
